/**
 * V-1304 (Mimmo, 02.11.2026): Angebot für den „4. Dezember 2020“ verschickt — das Jahr war beim
 * Anlegen falsch eingegeben, niemand hat es gesehen, und der Verfallslauf hat den frischen Vorgang
 * eine Stunde später als „verfallen“ abgesagt. Dieser Hinweis steht überall dort, wo das Datum
 * eines Vorgangs gesetzt oder verschickt wird (Anlege-Formular, Sende-Dialog): er nennt das Datum,
 * schlägt das nächste Vorkommen desselben Tages vor und korrigiert mit einem Klick.
 *
 * Bewusst ein HINWEIS und keine Sperre: Nachträge zu vergangenen Veranstaltungen gibt es wirklich.
 * Die Regel selbst (`datumInVergangenheit`) liegt in `@maestro/feiertage`, damit Server und
 * Oberfläche gleich entscheiden.
 */
import { datumInVergangenheit, heuteInZone } from "@maestro/feiertage";
import { Button, Checkbox, Icon, datumIsoZuDe } from "./components";

export function vergangenesDatum(datumIso: string | null | undefined, jetzt: Date = new Date(), endeIso?: string | null) {
  return datumInVergangenheit(datumIso, heuteInZone("Europe/Berlin", jetzt), endeIso);
}

/** Offene Vorgangs-Status: nur hier ist ein vergangenes Datum ein „Datum prüfen“ (danach ist es Geschichte). */
const OFFEN = new Set(["inquiry", "offer_draft", "offer_sent"]);

/**
 * V-1304: Karte/Zeile eines OFFENEN Vorgangs mit Datum in der Vergangenheit → „Datum prüfen“.
 * `eventStatus` fehlt (noch kein Vorgang angelegt) zählt als offen.
 */
export function datumPruefenNoetig(datumIso: string | null | undefined, eventStatus: string | null | undefined, jetzt: Date = new Date(), endeIso?: string | null): boolean {
  if (eventStatus && !OFFEN.has(eventStatus)) return false;
  return vergangenesDatum(datumIso, jetzt, endeIso) != null;
}

export function DatumVergangenHinweis({
  datumIso,
  endeIso,
  onKorrigieren,
  bestaetigt,
  onBestaetigt,
  testId = "datum-vergangen-hinweis",
}: {
  datumIso: string | null | undefined;
  /** Enddatum einer mehrtägigen Veranstaltung: die Prüfung gilt dem Ende, die Korrektur verschiebt beide. */
  endeIso?: string | null;
  /** Übernimmt den Vorschlag (ISO, bei mehrtägig mit verschobenem Ende). Fehlt er, gibt es keinen Korrektur-Knopf. */
  onKorrigieren?: (iso: string, isoEnde?: string) => void;
  /** Mit Bestätigungs-Haken (Sende-Dialog): „Datum stimmt so“. */
  bestaetigt?: boolean;
  onBestaetigt?: (ja: boolean) => void;
  testId?: string;
}) {
  const befund = vergangenesDatum(datumIso, undefined, endeIso);
  if (!befund) return null;
  const vorschlag = befund.vorschlag;
  return (
    <div
      role="status"
      data-testid={testId}
      className="mt-1.5 flex items-start gap-2 rounded-lg border border-[#E7C98A] bg-[#FBF3E2] px-2.5 py-2 text-[12px] text-[#6B4E12] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#E9D3A4]"
    >
      <Icon name="event_busy" className="mt-px shrink-0 text-[15px]" />
      <div className="min-w-0 space-y-1.5">
        <p>
          Datum liegt in der Vergangenheit: <strong>{datumIsoZuDe(befund.datum)}{befund.ende ? ` – ${datumIsoZuDe(befund.ende)}` : ""}</strong>
          {vorschlag ? <> – meintest du {datumIsoZuDe(vorschlag)}{befund.vorschlagEnde ? ` – ${datumIsoZuDe(befund.vorschlagEnde)}` : ""}?</> : null}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {vorschlag && onKorrigieren && (
            <Button type="button" variant="secondary" className="!px-2.5 !py-1 text-[12px]" data-testid={`${testId}-korrigieren`} onClick={() => (befund.vorschlagEnde ? onKorrigieren(vorschlag, befund.vorschlagEnde) : onKorrigieren(vorschlag))}>
              Auf {datumIsoZuDe(vorschlag)}{befund.vorschlagEnde ? ` – ${datumIsoZuDe(befund.vorschlagEnde)}` : ""} ändern
            </Button>
          )}
          {onBestaetigt && (
            <label className="inline-flex items-center gap-1.5">
              <Checkbox checked={!!bestaetigt} data-testid={`${testId}-bestaetigen`} onChange={(e) => onBestaetigt(e.target.checked)} />
              Datum stimmt so (Nachtrag zu einer vergangenen Veranstaltung)
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
